/**
 * CookieConsent — small dismissable banner shown to every visitor (anon
 * + authed) until they Accept or Decline. Persists in localStorage so
 * it doesn't nag on every page load.
 *
 * This is distinct from ConsentGate (which is the *authenticated*
 * data-processing signature — a legal document tied to a user account).
 * Cookies are a per-browser preference, so the two live side by side.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';

const KEY = 'cookie-consent-v1';   // bump if wording / choices change

// Routes where the banner MUST not appear — e.g. the Worship presenter
// console and output windows that are projected onto real screens.
const SUPPRESSED_PREFIXES = ['/worship/output/', '/worship/present/', '/portal/'];

export default function CookieConsent() {
  const [choice, setChoice] = useState(null);   // null = undecided, 'accepted' | 'declined'
  const [mounted, setMounted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    try { setChoice(localStorage.getItem(KEY)); } catch {}
    setMounted(true);
  }, []);

  const set = (val) => {
    try { localStorage.setItem(KEY, val); } catch {}
    setChoice(val);
    // Let any analytics-tracking script listen for this
    window.dispatchEvent(new CustomEvent('cookie-consent', { detail: val }));
  };

  const suppressed = SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p));
  // The banner is fixed to the bottom, so tell the page to leave room for it —
  // otherwise the last card on a phone sits permanently underneath it.
  const showing = mounted && !choice && !suppressed;
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('cookie-banner-open', showing);
    return () => root.classList.remove('cookie-banner-open');
  }, [showing]);

  // A hall screen has nobody to click it and no cookies worth consenting to
  // — it only ever reads the public calendar (iter 616).
  if (typeof window !== 'undefined'
      && /\/church\/[^/]+\/screen\/?$/.test(window.location.pathname)) {
    return null;
  }

  if (!showing) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      className="fixed bottom-[4.75rem] left-2 right-2 md:left-6 md:right-6 md:bottom-6 z-[9999] pointer-events-none"
      data-testid="cookie-consent"
    >
      <div className="pointer-events-auto max-w-3xl mx-auto bg-slate-900 text-slate-100 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
        <div className="p-2.5 md:p-5 flex flex-row items-center gap-2 md:gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="hidden md:flex h-10 w-10 rounded-lg bg-slate-800 items-center justify-center shrink-0">
              <Cookie className="h-5 w-5 text-amber-300" />
            </div>
            <p className="text-[11px] md:text-sm leading-snug md:leading-relaxed">
              <span className="hidden md:inline">We use cookies to enhance your browsing experience and keep your data secure. See our{' '}</span>
              <span className="md:hidden">We use cookies. See our{' '}</span>
              <Link to="/privacy" className="underline hover:text-white" data-testid="cookie-privacy-link">Privacy Policy</Link>{' '}·{' '}
              <Link to="/cookies" className="underline hover:text-white" data-testid="cookie-policy-link">Cookie Policy</Link>.
            </p>
          </div>
          <div className="flex gap-1.5 md:gap-2 shrink-0 w-auto">
            <button
              type="button"
              onClick={() => set('declined')}
              className="hidden md:inline-flex md:flex-initial px-3 py-2 rounded-md text-xs font-medium text-slate-300 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
              data-testid="cookie-decline"
            >
              Decline non-essential
            </button>
            <button
              type="button"
              onClick={() => set('accepted')}
              className="md:flex-initial px-3 md:px-4 py-1.5 md:py-2 rounded-md text-[11px] md:text-xs font-semibold bg-amber-400 hover:bg-amber-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="cookie-accept"
            >
              Accept<span className="hidden md:inline"> all</span>
            </button>
            <button
              type="button"
              onClick={() => set('declined')}
              className="p-2 rounded-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
              aria-label="Dismiss cookie banner"
              data-testid="cookie-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Helper for downstream analytics: returns 'accepted' | 'declined' | null. */
export function getCookieConsent() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}
